'use client';

import * as React from 'react';
import { buildShareUrl, copyToClipboard } from './sessionParams';

export interface ShareLinkButtonProps {
  roomName: string;
  className?: string;
}

/**
 * Copy this meeting's link.
 *
 * The link is rebuilt from the room name rather than taken from the address
 * bar, so it can never carry whatever the current visitor happens to have in
 * their query string — including the `u` / `o` / `metadata` identity that used
 * to make "share the link" mean "share my account".
 */
export function ShareLinkButton({ roomName, className }: ShareLinkButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const resetTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const onCopy = React.useCallback(async () => {
    const ok = await copyToClipboard(buildShareUrl(roomName));
    if (!ok) {
      // Copying is blocked often enough (permissions, insecure origin) that
      // failing silently would leave people clicking a dead button. Falling
      // back to a prompt at least lets them select the link by hand.
      window.prompt('Copy this meeting link:', buildShareUrl(roomName));
      return;
    }
    setCopied(true);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 2000);
  }, [roomName]);

  return (
    <button
      type="button"
      onClick={onCopy}
      title="Copy the link to this meeting"
      className={['lk-share-link-button', className].filter(Boolean).join(' ')}
      aria-live="polite"
    >
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          fill="currentColor"
          d="M6.354 9.646a.5.5 0 0 0 .708.708l2.5-2.5a2.5 2.5 0 0 0-3.536-3.536L4.44 5.904a.5.5 0 1 0 .707.707l1.586-1.586a1.5 1.5 0 1 1 2.122 2.122l-2.5 2.5Z"
        />
        <path
          fill="currentColor"
          d="M9.646 6.354a.5.5 0 0 0-.708-.708l-2.5 2.5a2.5 2.5 0 0 0 3.536 3.536l1.586-1.586a.5.5 0 0 0-.707-.707l-1.586 1.586a1.5 1.5 0 1 1-2.122-2.122l2.5-2.5Z"
        />
      </svg>
      {copied ? 'Link copied' : 'Copy link'}
    </button>
  );
}
